import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { FileDropzone } from '../../../../../components/ui/FileDropzone';
import { uploadSlots } from '../../../../../features/slots/slotsThunks';
import {
  SLOT_TEMPLATE_COLUMNS,
  SLOT_TEMPLATE_EXAMPLE,
} from '../../../../../features/slots/slotsConstants';

/** The server reads the sheet with openpyxl, which does not open the old `.xls`. */
const SHEET_EXTENSIONS = ['.xlsx'];

/** How many of the sheet's problems are listed before "y N más". */
const PROBLEMS_SHOWN = 12;

const downloadTemplate = () => {
  const sheet = XLSX.utils.json_to_sheet(SLOT_TEMPLATE_EXAMPLE, { header: SLOT_TEMPLATE_COLUMNS });
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Ubicaciones');
  XLSX.writeFile(book, 'plantilla-ubicaciones.xlsx');
};

/**
 * Loads a sheet of locations into the warehouse in use.
 *
 * A real warehouse has a hundred locations, and the form makes one at a time.
 * The warehouse is the one picked above, never a cell: the server checks once
 * that whoever uploads holds it. The load is all or nothing, and when the
 * sheet has problems the server names every one with its row, so the sheet
 * goes back once and not five times.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} props.warehouse - The warehouse in use.
 * @param {(message: string) => void} props.onDone - Told the server's own summary.
 */
export const SlotsUploadModal = ({ isOpen, onClose, warehouse, onDone }) => {
  const dispatch = useDispatch();
  const uploading = useSelector((state) => state.slots.uploading);

  const [file, setFile] = useState(null);
  const [failure, setFailure] = useState(null);

  // Every opening starts without a file.
  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setFailure(null);
  }, [isOpen]);

  const problem = (() => {
    if (!warehouse) return 'Elige la bodega';
    if (!file) return 'Elige la planilla';
    return null;
  })();

  const handleUpload = async () => {
    setFailure(null);
    const action = await dispatch(uploadSlots({ warehouseId: warehouse.id, file }));

    if (action.meta.requestStatus !== 'fulfilled') {
      // Nothing went in. The sheet stays staged so it is clear which one failed.
      setFailure(action.payload);
      return;
    }

    onDone(action.payload.message);
    onClose();
  };

  const problems = failure?.errors || [];
  const hidden = Math.max(problems.length - PROBLEMS_SHOWN, 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Carga masiva de ubicaciones"
      testId="slots-upload-modal"
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {warehouse ? `En ${warehouse.name}` : 'Elige arriba la bodega donde van las ubicaciones'}
      </p>

      <div className="space-y-4">
        {failure && (
          <div
            data-testid="slots-upload-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm space-y-2"
          >
            <p>{failure.message}</p>
            {problems.length > 0 && (
              <ul className="list-disc pl-5 space-y-0.5 text-xs">
                {problems.slice(0, PROBLEMS_SHOWN).map((item, index) => (
                  <li key={`${item.row}-${index}`} data-testid="slots-upload-problem">
                    Fila {item.row}: {item.message}
                  </li>
                ))}
                {hidden > 0 && <li>y {hidden} más</li>}
              </ul>
            )}
          </div>
        )}

        <FileDropzone
          file={file}
          onFileSelected={(selected) => {
            setFile(selected);
            setFailure(null);
          }}
          onClear={() => setFile(null)}
          extensions={SHEET_EXTENSIONS}
          hint="Planilla .xlsx con la columna Ubicación; el resto es opcional"
          disabled={uploading}
        />

        <div className="text-xs text-text-muted space-y-1">
          <p>
            Columnas: <strong>Ubicación</strong> obligatoria. Código (el que se escanea), Zona
            (Recepción, Almacenaje, Picking, Despacho o Cuarentena; vacía es Almacenaje),
            Recorrido y Capacidad opcionales.
          </p>
          <p>
            Los nombres no se repiten en la bodega. Es todo o nada: si una fila falla, no entra
            ninguna, y se te dicen todas las filas con problemas de una vez.
          </p>
        </div>

        <Button
          variant="ghost"
          size="sm"
          icon={Download}
          onClick={downloadTemplate}
          data-testid="slots-upload-template"
        >
          Descargar plantilla
        </Button>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={uploading}>
          Cancelar
        </Button>
        {/* Disabled, it says what is missing. */}
        <Button
          variant="primary"
          loading={uploading}
          loadingLabel="Cargando..."
          disabled={Boolean(problem)}
          onClick={handleUpload}
          data-testid="slots-upload-submit"
        >
          {problem || 'Cargar planilla'}
        </Button>
      </div>
    </Modal>
  );
};
